import React from 'react';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react';
import { MuiThemeProvider, makeStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import Box from '@material-ui/core/Box';
import CssBaseline from '../CssBaseline';
import depiTheme from '../theme/depi';
import depvTheme from '../theme/depv';
import enplusTheme from '../theme/enplus';
import useStores from '../utils/useStores';
import AdminMenu from '@studionand/ada-next/src/components/AdminMenu';
import CookieBanner from './CookieBanner';
import {
  initGA,
  logPageViewGA,
  initFP,
  logPageViewFP,
  logPageViewGA4,
  initGA4,
  initOutbrain,
  logPageViewOb,
  initGTM,
} from '../utils/analytics';
import Breakpoint from './Breakpoint';

const useStyles = makeStyles(theme => ({
  progress: {
    position: 'fixed',
    zIndex: 9999,
    width: '100%',
  },
}));

const getOffset = context => {
  switch (context) {
    case 'pellets':
    case 'hackschnitzel':
    case 'holzbriketts':
      return 0;
    default:
      return 0;
  }
};

const getTheme = context => {
  switch (context) {
    case 'pellets':
    case 'hackschnitzel':
    case 'holzbriketts':
      return enplusTheme;
    case 'depi':
      return depiTheme;
    case 'depv':
      return depvTheme;
    default:
      return enplusTheme;
  }
};

const getGaId = context => {
  switch (context) {
    case 'pellets':
      return 'UA-40578229-4';
    case 'hackschnitzel':
      return 'UA-40578229-15';
    case 'holzbriketts':
      return 'UA-40578229-5';
    case 'depi':
      return 'UA-40578229-1';
    case 'depv':
      return 'UA-40578229-2';
  }
};

const getGa4Id = context => {
  switch (context) {
    case 'pellets':
      return 'G-GEV2HK4L8E';
    case 'hackschnitzel':
      return 'G-17ZBPRX35L';
    case 'holzbriketts':
      return 'G-EDTHB0J49H';
    case 'depi':
      return 'G-V4GMMZ6CFN';
    case 'depv':
      return 'G-PJGYYBEDJ0';
  }
};

const getFpId = context => {
  switch (context) {
    case 'depi':
      return '712152723695159';
    default:
      return null;
  }
};

const AppWrapper = ({ children, ...props }) => {
  const classes = useStyles();
  const router = useRouter();
  const { uiStore, dataStore } = useStores();
  const offset = getOffset(uiStore.context);
  const theme = getTheme(uiStore.context);
  const gaId = getGaId(uiStore.context);
  const fpId = getFpId(uiStore.context);
  const ga4Id = getGa4Id(uiStore.context);
  const isLoading =
    dataStore.isLoading ||
    dataStore.companies.isLoading ||
    dataStore.pages.isLoading ||
    dataStore.isSearching;

  const handleLogPageView = () => {
    if (uiStore.gaCookiesAccepted || uiStore.functionalCookiesAccepted) {
      logPageViewGA();
      logPageViewGA4();
    }
    if (
      fpId &&
      (uiStore.fpCookiesAccepted || uiStore.functionalCookiesAccepted)
    ) {
      logPageViewFP();
    }
    if (uiStore.obCookiesAccepted) {
      logPageViewOb();
    }
  };

  React.useEffect(() => {
    if (uiStore.gaCookiesAccepted || uiStore.functionalCookiesAccepted) {
      initGA(
        gaId,
        uiStore.googleAdsAccepted || uiStore.marketingCookiesAccepted,
      );
      initGA4(
        ga4Id,
        uiStore.googleAdsAccepted || uiStore.marketingCookiesAccepted,
      );
    }
    if (
      fpId &&
      (uiStore.fpCookiesAccepted || uiStore.functionalCookiesAccepted)
    )
      initFP(fpId);

    if (uiStore.obCookiesAccepted) {
      initOutbrain();
    }
    if (
      uiStore.gaCookiesAccepted ||
      uiStore.fpCookiesAccepted ||
      uiStore.obCookiesAccepted ||
      uiStore.functionalCookiesAccepted
    ) {
      handleLogPageView();
      router.events.on('routeChangeComplete', handleLogPageView);
      return () => {
        router.events.off('routeChangeComplete', handleLogPageView);
      };
    }
  }, [
    uiStore.gaCookiesAccepted,
    uiStore.fpCookiesAccepted,
    uiStore.functionalCookiesAccepted,
    uiStore.marketingCookiesAccepted,
    uiStore.googleAdsAccepted,
    uiStore.obCookiesAccepted,
  ]);

  React.useEffect(() => {
    if (uiStore.context !== 'depi') return;
    initGTM();
  }, [uiStore.context]);

  return (
    <React.Fragment>
      <MuiThemeProvider theme={theme}>
        {isLoading && <LinearProgress className={classes.progress} />}
        <CssBaseline />
        <AdminMenu offset={offset} />
        {children}
        <CookieBanner />
        <Breakpoint />
      </MuiThemeProvider>
    </React.Fragment>
  );
};

export default observer(AppWrapper);
